import { useRef, useState } from 'react';
import { useCheckSlugAvailability } from '@/features/permissions/hooks/useCheckSlugAvailability';
import { useUpdateExperienceSlug } from '@/features/permissions/hooks/useUpdateExperienceSlug';
import { useDebounceLoading } from '@/hooks/useDebounceLoading';
import { isValidSlug } from '../utils';
import { useCustomSlugForExperience } from './useCustomSlugForExperience';

export const useEditableLinkState = (
  publishedSlug?: string,
  experienceId?: string
) => {
  const [isEditing, setIsEditing] = useState(false);
  const [slug, setSlug] = useState(publishedSlug || '');
  const [validSlug, setValidSlug] = useState(publishedSlug || '');
  const [clientValidationError, setClientValidationError] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTrigger, setModalTrigger] = useState<'blur' | 'update'>('blur');

  const { hasCustomSlugForPage, setCustomSlug } = useCustomSlugForExperience();
  const hasCustomSlug = hasCustomSlugForPage(experienceId || '');

  const { isSlugAvailable, validationError, isPending } =
    useCheckSlugAvailability(validSlug);
  const { updateSlug, isError } = useUpdateExperienceSlug();

  const isUpdateButtonClickedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedIsPending = useDebounceLoading(isPending);

  const error =
    clientValidationError || validationError || !isSlugAvailable || isError;

  const getErrorMessage = () => {
    const invalidSlug = clientValidationError || validationError;
    if (publishedSlug === slug) {
      return '';
    }
    if (!invalidSlug && isSlugAvailable) {
      return 'This URL is available';
    }
    if (invalidSlug) {
      return 'URLs must be at least 3 characters and contain only letters, numbers, or dashes';
    }
    if (!isSlugAvailable) {
      return 'This URL is already taken';
    }
    if (isError) {
      return 'There was an error securing the URL. Please try again.';
    }
    return '';
  };

  const errorMessage = isEditing ? getErrorMessage() : '';

  const handleOnChange = (newSlug: string) => {
    const normalizedSlug = newSlug.toLowerCase();
    setIsEditing(true);
    setIsUpdated(false);
    setSlug(normalizedSlug);

    if (normalizedSlug.length >= 3 && isValidSlug(normalizedSlug)) {
      setClientValidationError(false);
      setValidSlug(normalizedSlug);
    } else {
      setClientValidationError(true);
    }
  };

  const handleOnBlur = () => {
    if (
      isEditing &&
      slug !== publishedSlug &&
      !isUpdateButtonClickedRef.current
    ) {
      setModalTrigger('blur');
      setIsModalOpen(true);
      return;
    }
    isUpdateButtonClickedRef.current = false;
  };

  const handleUpdateSlug = () => {
    if (isEditing && slug !== publishedSlug && hasCustomSlug) {
      setModalTrigger('update');
      setIsModalOpen(true);
      return;
    }
    updateSlug(slug, {
      onSuccess: () => {
        setCustomSlug(slug, experienceId);
        setIsEditing(false);
        setIsUpdated(true);
      },
      onError: (error) => {
        console.error('Failed to update slug:', error);
      },
    });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 1000);
  };

  const handleConfirm = () => {
    setIsModalOpen(false);

    if (modalTrigger === 'blur') {
      setSlug(publishedSlug || '');
    } else if (modalTrigger === 'update') {
      updateSlug(slug);
    }

    setIsEditing(false);
  };

  const handleFocusInput = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    isUpdateButtonClickedRef.current = true;
  };

  return {
    isEditing,
    slug,
    isUpdated,
    isModalOpen,
    modalTrigger,
    error,
    errorMessage,
    debouncedIsPending,
    inputRef,
    handleOnChange,
    handleOnBlur,
    handleUpdateSlug,
    handleCancel,
    handleConfirm,
    handleFocusInput,
  };
};
